import { Error } from "@mui/icons-material";
import { Grid, Box, Typography, Divider } from "@mui/material";
import { TitledSection } from "components/Classifiers/Common/TitledSection";
import Markdown from "markdown-to-jsx";
import {
    TestLLMClassifierCancelledError,
    TestLLMClassifierError,
    TestLLMClassifierErrorType,
    TestLLMClassifierInternalServiceError,
    TestLLMClassifierNoResultsError,
    TestLLMClassifierResultOutOfRangeError,
} from "models/LLMs/Testing/TestLLMClassifierError";
import React from "react";
import {
    LLMExecutionResultInformation,
    LLMExecutionResultProvider,
} from "./LLMExecutionResultInformation";
import AcxChip from "components/UI/AcxChip";
import { LLMReasoning } from "./LLMReasoning";

type LLMExecutorErrorProps = {
    error: TestLLMClassifierError;
};

export function ErrorExplanation(props: LLMExecutorErrorProps) {
    return (
        <TitledSection
            title={
                <Grid
                    container
                    component="span"
                    alignItems="center"
                    columnGap={1}
                    fontSize="12px"
                >
                    <Error color="error" />
                    <Grid item>{props.error.message}</Grid>
                </Grid>
            }
            collapsible
            defaultCollapsed={true}
            variant="bordered"
        >
            {props.error.type ===
                TestLLMClassifierErrorType.InternalServiceError && (
                <InternalServiceErrorDetails error={props.error} />
            )}
            {props.error.type === TestLLMClassifierErrorType.NoResults && (
                <NoResultsErrorDetails error={props.error} />
            )}
            {props.error.type ===
                TestLLMClassifierErrorType.ResultOutOfRange && (
                <ResultOutOfRangeErrorDetails error={props.error} />
            )}
            {props.error.type === TestLLMClassifierErrorType.Cancelled && (
                <InternalServiceErrorDetails error={props.error} />
            )}
            {props.error.type <
                TestLLMClassifierErrorType.InternalServiceError ||
                (props.error.type > TestLLMClassifierErrorType.Cancelled && (
                    <Box
                        component="pre"
                        sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}
                    >
                        {JSON.stringify(props.error.metadata, null, 2)}
                    </Box>
                ))}
        </TitledSection>
    );
}

export function InternalServiceErrorDetails(props: {
    error: TestLLMClassifierInternalServiceError | TestLLMClassifierCancelledError;
}) {
    const metadata = props.error.metadata;
    const message = metadata.message ?? metadata.Message ?? "";
    const exceptionType = metadata.exceptionType ?? metadata.ExceptionType;
    const innerExceptionMessage =
        metadata.innerExceptionMessage ?? metadata.InnerExceptionMessage;
    const fullException = metadata.fullException ?? metadata.FullException;

    return (
        <Grid container direction="column" rowGap={2}>
            <Grid container direction="column" rowGap={1}>
                <Grid item>
                    <Typography fontWeight="bold">Message:</Typography>
                </Grid>
                <Grid item pl={2}>
                    <Box
                        component="pre"
                        sx={{
                            fontFamily: "monospace",
                            fontSize: "12px",
                            margin: 0,
                            overflowX: "auto",
                            whiteSpace: "pre-wrap",
                        }}
                    >
                        {message}
                    </Box>
                </Grid>
            </Grid>
            {exceptionType && (
                <React.Fragment>
                    <Divider />
                    <Grid container direction="column" rowGap={1}>
                        <Grid item>
                            <Typography fontWeight="bold">
                                Exception Type:
                            </Typography>
                        </Grid>
                        <Grid item pl={2}>
                            <Markdown>{exceptionType}</Markdown>
                        </Grid>
                    </Grid>
                </React.Fragment>
            )}
            {innerExceptionMessage && (
                <React.Fragment>
                    <Divider />
                    <Grid container direction="column" rowGap={1}>
                        <Grid item>
                            <Typography fontWeight="bold">
                                Inner Exception Message:
                            </Typography>
                        </Grid>
                        <Grid item pl={2}>
                            <Markdown>
                                {innerExceptionMessage}
                            </Markdown>
                        </Grid>
                    </Grid>
                </React.Fragment>
            )}
            {fullException && (
                <React.Fragment>
                    <Divider />
                    <Grid container direction="column" rowGap={1}>
                        <Grid item>
                            <Typography fontWeight="bold">
                                Full Exception:
                            </Typography>
                        </Grid>
                        <Grid item pl={2}>
                            <Box
                                component="pre"
                                sx={{
                                    fontFamily: "monospace",
                                    fontSize: "12px",
                                    margin: 0,
                                    overflowX: "auto",
                                    whiteSpace: "pre-wrap",
                                }}
                            >
                                {fullException}
                            </Box>
                        </Grid>
                    </Grid>
                </React.Fragment>
            )}
        </Grid>
    );
}

export function NoResultsErrorDetails(props: {
    error: TestLLMClassifierNoResultsError;
}) {
    return (
        <Grid container direction="column" rowGap={2}>
            <LLMExecutionResultProvider
                executionResult={{
                    stepVariables: props.error.metadata.stepVariables,
                    classifierResultsToWrite:
                        props.error.metadata.classifierResultsToWrite,
                    extendedMetadataToWrite:
                        props.error.metadata.extendedMetadataToWrite,
                }}
            >
                <LLMExecutionResultInformation.StepVariables />
                <LLMExecutionResultInformation.ClassifierResultsToWrite />
                <LLMExecutionResultInformation.ExtendedMetadataToWrite />
            </LLMExecutionResultProvider>
        </Grid>
    );
}

export function ResultOutOfRangeErrorDetails(props: {
    error: TestLLMClassifierResultOutOfRangeError;
}) {
    return (
        <Grid container direction="column" rowGap={2}>
            <Grid container item alignItems="center" columnGap={1}>
                <Grid item fontWeight="bold">
                    Allowed Results:
                </Grid>
                {props.error.metadata.allowedResults.map((result) => (
                    <Grid item>
                        <AcxChip
                            color="lightBlue"
                            label={result}
                            size="small"
                        />
                    </Grid>
                ))}
            </Grid>
            <Grid container item alignItems="center" columnGap={1}>
                <Grid item fontWeight="bold">
                    Actual Result:
                </Grid>
                <Grid item>
                    <AcxChip
                        color="red"
                        label={
                            props.error.metadata.actualResult ?? "None Written"
                        }
                        size="small"
                    />
                </Grid>
            </Grid>
            {props.error.metadata.reasoning && (
                <LLMReasoning reasoning={props.error.metadata.reasoning} />
            )}
            <LLMExecutionResultProvider
                executionResult={{
                    classifierResultsToWrite:
                        props.error.metadata.classifierResultsToWrite,
                    extendedMetadataToWrite:
                        props.error.metadata.extendedMetadataToWrite,
                }}
            >
                {props.error.metadata.classifierResultsToWrite && (
                    <LLMExecutionResultInformation.ClassifierResultsToWrite />
                )}
                {props.error.metadata.extendedMetadataToWrite && (
                    <LLMExecutionResultInformation.ExtendedMetadataToWrite />
                )}
            </LLMExecutionResultProvider>
        </Grid>
    );
}
